/**
 * A munkamenet állapota. A JOGOSULTSÁGRÓL itt nem születik döntés — azt a
 * szerver mondja meg minden kérésnél. A felület csak megjeleníti, amit a
 * `/auth/me` visszaad.
 *
 * Ez szándékos: ha a kliens döntené el, mit lát, a szűkítés a kliensen múlna.
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { api, type LoginResponse, type MagicLinkCheck, type Me } from '@/lib/api-client';
import { forgetCompanySelection } from '@/lib/company-selection';

/**
 * Miért ért véget a munkamenet — ha NEM a felhasználó zárta le.
 *
 * Azért unió és nem `boolean`, mert a belépőképernyőnek meg kell tudnia
 * mondani, MI történt. Egy szó nélkül visszadobott ember azt hiszi, elrontott
 * valamit; a `'expired'` az egyetlen ág ma, de a bővítéskor a fordító sorolja
 * fel a helyeket, ahol dönteni kell.
 */
export type SignedOutReason = 'expired';

interface AuthState {
  me: Me | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  /** „Él-e még ez a meghívó-link, és kié?" — nem használja el a tokent. */
  checkMagicLink: (token: string) => Promise<MagicLinkCheck>;
  /**
   * Beváltás: jelszót állít, és RÖGTÖN be is léptet a friss jelszóval.
   *
   * ⚠️ A `'passwordSet'` NEM HIBA. A jelszó elmentődött és a token elhasználódott,
   * csak a munkamenet nem állt fel (a `/auth/me` bukott). A képernyőnek ezt ki
   * kell mondania: egy újrapróbálás már „lejárt link"-et kapna.
   */
  redeemMagicLink: (token: string, password: string) => Promise<'signedIn' | 'passwordSet'>;
  /**
   * Kiléptetés. Indok NÉLKÜL hívva ez a felhasználó saját döntése (a fejléc
   * gombja); indokkal a rendszeré — és akkor a belépőképernyő meg is mondja,
   * miért van ott.
   */
  signOut: (reason?: SignedOutReason) => void;
  /** `null`, ha a felhasználó maga lépett ki, vagy még be sem lépett. */
  signedOutReason: SignedOutReason | null;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [signedOutReason, setSignedOutReason] = useState<SignedOutReason | null>(null);

  /** A friss `me`-t vissza is adja — a beváltásnak tudnia kell, felállt-e. */
  const refresh = useCallback(async (): Promise<Me | null> => {
    try {
      const next = await api<Me>('/auth/me');
      setMe(next);
      return next;
    } catch {
      setMe(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      await api<LoginResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      // Az indok a MÚLTÉ, amint valaki újra belépett. Enélkül a következő
      // kilépés-belépés körben is ott állna a „lejárt a munkameneted" felirat.
      setSignedOutReason(null);
      await refresh();
    },
    [refresh]
  );

  const checkMagicLink = useCallback(
    (token: string) =>
      api<MagicLinkCheck>('/auth/magic-link/check', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
    []
  );

  /**
   * A BEVÁLTÁS UGYANOTT VÉGZŐDIK, AHOL A BELÉPÉS: munkamenet-tokennel és egy
   * friss `/auth/me`-vel. Ezért van itt, és nem a képernyőben — a munkamenet
   * állapotát egyetlen hely írja (a PWA `AuthProvider`-ének mintájára).
   */
  const redeemMagicLink = useCallback(
    async (token: string, password: string) => {
      await api<LoginResponse>('/auth/magic-link/redeem', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      setSignedOutReason(null);
      return (await refresh()) === null ? 'passwordSet' : 'signedIn';
    },
    [refresh]
  );

  /**
   * ⚠️ A KILÉPÉS MOSTANTÓL SZERVER-HÍVÁS (#14). A munkamenet `httpOnly`
   * sütiben van, amit a JS nem tud törölni — csak a szerver `Set-Cookie`-ja.
   * A korábbi alak a kliens példányát törölte, és a szerver nem tudott róla:
   * a token a lejártáig érvényes maradt.
   *
   * A hívás hibáját ELNYELJÜK, és a felület akkor is kilép. Aki ki akar
   * lépni, annak a képernyő nem maradhat nyitva azért, mert a hálózat
   * éppen döglött — a süti a böngésző bezárásáig él, de a felhasználó
   * szándéka egyértelmű volt.
   */
  const signOut = useCallback((reason?: SignedOutReason) => {
    void api<void>('/auth/logout', { method: 'POST' }).catch(() => undefined);
    // A CÉGVÁLASZTÁS IS A MUNKAMENETTEL MEGY (#76). Egy közös gépen a
    // következő belépő ne a régi cégre szűkítve, és ne az előző könyvelő
    // „legutóbbi" cégeivel induljon.
    forgetCompanySelection();
    setMe(null);
    setSignedOutReason(reason ?? null);
  }, []);

  const value = useMemo(
    () => ({ me, loading, signIn, checkMagicLink, redeemMagicLink, signOut, signedOutReason }),
    [me, loading, signIn, checkMagicLink, redeemMagicLink, signOut, signedOutReason]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('A useAuth csak AuthProvider-en belül használható.');
  }
  return context;
}
